/**
 * Security Utilities
 * Common security functions for the application
 */

/**
 * Sanitize user input to prevent XSS attacks
 */
export function sanitizeInput(input: string): string {
  if (typeof input !== 'string') return '';
  
  return input
    .replace(/[<>]/g, '') // Remove potential HTML tags
    .replace(/javascript:/gi, '') // Remove javascript: protocol
    .replace(/on\w+=/gi, '') // Remove event handlers
    .trim();
}

/**
 * Validate email format
 */
export function isValidEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

/**
 * Validate password strength
 */
export function validatePasswordStrength(password: string): {
  isValid: boolean;
  score: number;
  feedback: string[];
} {
  const feedback: string[] = [];
  let score = 0;

  if (password.length >= 8) {
    score += 1;
  } else {
    feedback.push('Password must be at least 8 characters long');
  }

  if (/[a-z]/.test(password)) {
    score += 1;
  } else {
    feedback.push('Password must contain lowercase letters');
  }

  if (/[A-Z]/.test(password)) {
    score += 1;
  } else {
    feedback.push('Password must contain uppercase letters');
  }

  if (/\d/.test(password)) {
    score += 1;
  } else {
    feedback.push('Password must contain numbers');
  }

  if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
    score += 1;
  } else {
    feedback.push('Password must contain special characters');
  }

  return {
    isValid: score >= 3,
    score,
    feedback
  };
}

/**
 * Secure storage wrapper
 */
export const secureStorage = {
  /**
   * Store data securely in localStorage with encryption-like obfuscation
   */
  setItem(key: string, value: string): void {
    try {
      const obfuscated = btoa(encodeURIComponent(value));
      localStorage.setItem(key, obfuscated);
    } catch (error) {
      console.error('Failed to store secure item:', error);
    }
  },

  /**
   * Retrieve and decrypt data from localStorage
   */
  getItem(key: string): string | null {
    try {
      const stored = localStorage.getItem(key);
      if (!stored) return null;
      
      return decodeURIComponent(atob(stored));
    } catch (error) {
      console.error('Failed to retrieve secure item:', error);
      return null;
    }
  },

  /**
   * Remove item from localStorage
   */
  removeItem(key: string): void {
    localStorage.removeItem(key);
  },

  /**
   * Clear all items from localStorage
   */
  clear(): void {
    localStorage.clear();
  }
};

/**
 * Content Security Policy helpers
 */
export const csp = {
  /**
   * Check if script source is allowed
   */
  isScriptAllowed(src: string): boolean {
    const allowedSources = [
      self.location.origin,
      'https://cdn.jsdelivr.net',
      'https://unpkg.com'
    ];
    
    return allowedSources.some(allowed => src.startsWith(allowed));
  },

  /**
   * Check if external link is safe
   */
  isExternalLinkSafe(url: string): boolean {
    try {
      const urlObj = new URL(url);
      const allowedProtocols = ['http:', 'https:', 'mailto:'];
      
      return allowedProtocols.includes(urlObj.protocol);
    } catch {
      return false;
    }
  }
};

/**
 * Rate limiting utilities
 */
export class RateLimiter {
  private attempts: Map<string, number[]> = new Map();

  /**
   * Check if action is allowed based on rate limit
   */
  isAllowed(key: string, maxAttempts: number = 5, windowMs: number = 300000): boolean {
    const now = Date.now();
    const windowStart = now - windowMs;
    
    // Get existing attempts for this key
    let keyAttempts = this.attempts.get(key) || [];
    
    // Remove attempts outside the time window
    keyAttempts = keyAttempts.filter(timestamp => timestamp > windowStart);
    
    // Check if we're within the limit
    if (keyAttempts.length >= maxAttempts) {
      return false;
    }
    
    // Record this attempt
    keyAttempts.push(now);
    this.attempts.set(key, keyAttempts);
    
    return true;
  }

  /**
   * Reset attempts for a key
   */
  reset(key: string): void {
    this.attempts.delete(key);
  }

  /**
   * Get remaining attempts
   */
  getRemainingAttempts(key: string, maxAttempts: number = 5, windowMs: number = 300000): number {
    const now = Date.now();
    const windowStart = now - windowMs;
    
    const keyAttempts = this.attempts.get(key) || [];
    const recentAttempts = keyAttempts.filter(timestamp => timestamp > windowStart);
    
    return Math.max(0, maxAttempts - recentAttempts.length);
  }
}

/**
 * CSRF protection utilities
 */
export const csrf = {
  /**
   * Generate CSRF token
   */
  generateToken(): string {
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);
    return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
  },

  /**
   * Set CSRF token in DOM
   */
  setToken(token: string): void {
    let metaTag = document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement;
    
    if (!metaTag) {
      metaTag = document.createElement('meta');
      metaTag.name = 'csrf-token';
      document.head.appendChild(metaTag);
    }
    
    metaTag.content = token;
  },

  /**
   * Get CSRF token from DOM
   */
  getToken(): string | null {
    const metaTag = document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement;
    return metaTag ? metaTag.content : null;
  }
};

/**
 * Input validation utilities
 */
export const validation = {
  /**
   * Validate file upload
   */
  isFileAllowed(file: File, allowedTypes: string[], maxSizeMB: number = 10): {
    isValid: boolean;
    errors: string[];
  } {
    const errors: string[] = [];
    
    // Check file type
    if (!allowedTypes.includes(file.type)) {
      errors.push(`File type ${file.type} is not allowed`);
    }
    
    // Check file size
    const maxSizeBytes = maxSizeMB * 1024 * 1024;
    if (file.size > maxSizeBytes) {
      errors.push(`File size must be less than ${maxSizeMB}MB`);
    }
    
    // Check file name
    if (!/^[a-zA-Z0-9._-]+$/.test(file.name)) {
      errors.push('File name contains invalid characters');
    }
    
    return {
      isValid: errors.length === 0,
      errors
    };
  },

  /**
   * Validate URL
   */
  isValidUrl(url: string): boolean {
    try {
      const urlObj = new URL(url);
      return ['http:', 'https:'].includes(urlObj.protocol);
    } catch {
      return false;
    }
  },

  /**
   * Validate phone number
   */
  isValidPhoneNumber(phone: string): boolean {
    const phoneRegex = /^[\+]?[1-9][\d]{0,15}$/;
    return phoneRegex.test(phone.replace(/[\s\-\(\)]/g, ''));
  }
};

// Export singleton rate limiter for login attempts
export const loginRateLimiter = new RateLimiter();